import Dexie from 'dexie';
import {
  updateTopSection,
  updateRightSection,
  updateBottomSection,
  updateLeftSection,
  toggleFullScreen,
} from '../../../sections/redux/sectionsSlice';
import { Section } from '../../../sections/types';
import { SheetHistory, UnSavedSheetHistory } from '../../../sheet/types';
import { addNewWindow, closeWindow } from '../../../windows/redux/windowsSlice';
import { AppWindow, WindowMetaData } from '../../../windows/types';
import { decrypt, encrypt } from '../../cryptor/cipher';
import {
  getGlobalDatabaseInstance,
  GlobalDatabases,
} from '../manager/databaseManager';
import {
  UserDBStores,
  UserStorageDataType,
  UserStorageFlags,
} from '../schemas';
import { sheetConstants } from '../../../windows/constants/sheetConstants';
import { UserStorageParams } from './types';
import { LockMechanism } from '../../utils/lock/lockMechanism';

export const getUserDBName = (userId: string) =>
  `${GlobalDatabases.User}/${userId}`;

// #region window
export const getActiveWindowId = async (db: Dexie) => {
  try {
    const id = await db
      .table(UserDBStores.Flags)
      .get(UserStorageFlags.ActiveWindowId);
    if (id) {
      return id;
    }
    return undefined;
  } catch (err) {
    return undefined;
  }
};

export async function saveWindowIDB(
  dbName: string,
  window: AppWindow,
  feKey: CryptoKey
) {
  const db = await getGlobalDatabaseInstance(dbName, GlobalDatabases.User);
  const encWindow = await encrypt(JSON.stringify(window), 'window', feKey);
  await db.table(UserDBStores.Windows).put(encWindow, window.id);


  await db
    .table(UserDBStores.Flags)
    .put(window.id, UserStorageFlags.ActiveWindowId);
}

// Delete a window by ID
export async function deleteWindowIDB(dbName: string, windowId: string) {
  const db = await getGlobalDatabaseInstance(dbName, GlobalDatabases.User);

  await db.table(UserDBStores.Windows).delete(windowId);
}

export const startSaveUserWindows = async (
  feKey: CryptoKey,
  dbName: string,
  actionType: string,
  windowId: string,
  window?: AppWindow
) => {
  switch (actionType) {
    case addNewWindow.toString():
      if (window) await saveWindowIDB(dbName, window, feKey);
      break;
    case closeWindow.toString():
      await deleteWindowIDB(dbName, windowId);
      break;
    default:
      if (window) await saveWindowIDB(dbName, window, feKey);
  }
};
// #endregion

//   #region sections
export async function saveSectionInDB(
  dbName: string,
  key: Section,
  section: string,
  feKey: CryptoKey
) {
  const db = await getGlobalDatabaseInstance(dbName, GlobalDatabases.User);
  const encSection = await encrypt(section, 'section', feKey);
  await db.table(UserDBStores.Sections).put(encSection, key);
}

const sectionTypeMap = {
  [updateTopSection.toString()]: Section.TopSection,
  [updateRightSection.toString()]: Section.RightSection,
  [updateBottomSection.toString()]: Section.BottomSection,
  [updateLeftSection.toString()]: Section.LeftSection,
  [toggleFullScreen.toString()]: Section.FullScreen,
};

export const startSaveUserSections = async (
  feKey: CryptoKey,
  dbName: string,
  actionType: string,
  sections: any
) => {
  const sectionType = sectionTypeMap[actionType];
  const sectionData = sections[sectionType];

  if (sectionData) {
    await saveSectionInDB(
      dbName,
      sectionType,
      JSON.stringify(sectionData),
      feKey
    );
  }
};
// #endregion

// #region write sheet history
// const deleteTopRecords = async (
//   db: Dexie,
//   storeName: string,
//   recordsToDelete: number
// ) => {
//   await db
//     .table(storeName)
//     .orderBy(':id')
//     .offset(0)
//     .limit(recordsToDelete)
//     .delete();
// };
// export const startSaveSheetHistory = async (
//   feKey: CryptoKey,
//   dbName: string,
//   newHistory: SheetHistory,
//   store: string,
//   oppositeStore: string,
//   clearOppositeStore?: boolean
// ) => {
//   const encryptedData = await encrypt(
//     JSON.stringify(newHistory),
//     'history',
//     feKey
//   );
//   const db = await getGlobalDatabaseInstance(dbName, GlobalDatabases.User);
//   await db.transaction('rw', db.tables, async () => {
//     // Clear Opposite store in case of simple History action future will be cleared
//     // Else Remove last record from opposite store in case of future (we're sort in reverse while reading)
//     if (clearOppositeStore) {
//       await db.table(oppositeStore).clear();
//     } else {
//       const itemCount = await db.table(oppositeStore).count();
//       if (itemCount > 0)
//         await db
//           .table(oppositeStore)
//           .offset(itemCount - 1)
//           .delete();
//     }

//     await db.table(store).put(encryptedData);

//     const recordsCount = await db.table(store).count();
//     if (recordsCount > sheetConstants.maxUndo) {
//       const recordsToDelete = recordsCount - sheetConstants.maxUndo;
//       // Delete Top Record
//       await deleteTopRecords(db, store, recordsToDelete);
//     }
//   });
// };
// #endregion

// #region read & compile sheet history
// export const readUserSheetHistory = async (
//   db: Dexie,
//   feKey: CryptoKey,
//   tableName: UserDBStores.Past | UserDBStores.Future
// ) => {
//   const history: SheetHistory[] = [];
//   await db
//     .table(tableName)
//     .orderBy(':id')
//     .each(async (encHistory, cursor) => {
//       const decHistory = await decrypt(encHistory, feKey);
//       history.push(JSON.parse(decHistory));
//     });
//   return history;
// };

// export const clearUserSheetHistory = async (db: Dexie) => {
//   await db.table(UserDBStores.Past).clear();
//   await db.table(UserDBStores.Future).clear();
// };
// #endregion

// #region start save user flags
export const saveUserFlags = async (
  dbName: string,
  flagKey: string,
  value: boolean | string
) => {
  const db = await getGlobalDatabaseInstance(dbName, GlobalDatabases.User);
  await db.table(UserDBStores.Flags).put(value, flagKey);
};
// #endregion

const lock = new LockMechanism();

export const readUserFlag = async (db: Dexie, flagKey: string) =>
  await db.table(UserDBStores.Flags).get(flagKey);

// #region save data
export const saveDataInUserStorage = async (params: UserStorageParams) => {
  const { actionType, userId, feKey, data } = params;
  const dbName = getUserDBName(userId);

  if (!data) return;

  await lock.acquire(data.dataType);

  try {
    switch (data.dataType) {
      case UserStorageDataType.Windows:
        await startSaveUserWindows(
          feKey,
          dbName,
          actionType,
          data.windowId,
          data.window
        );
        break;
      case UserStorageDataType.Sections:
        await startSaveUserSections(feKey, dbName, actionType, data.sections);
        break;
      // case UserStorageDataType.Undo:
      //   await startSaveSheetHistory(
      //     feKey,
      //     dbName,
      //     data.past,
      //     UserDBStores.Future,
      //     UserDBStores.Past
      //   );
      //   break;
      // case UserStorageDataType.Redo:
      // case UserStorageDataType.History:
      //   await startSaveSheetHistory(
      //     feKey,
      //     dbName,
      //     data.past,
      //     UserDBStores.Past,
      //     UserDBStores.Future,
      //     data.dataType === UserStorageDataType.History
      //   );
      //   break;
      case UserStorageDataType.Flags:
        await saveUserFlags(dbName, data.flagKey, data.flagValue);
    }
  } catch (e) {
    console.log('error while saving', e);
  }
  await lock.release(data.dataType);
};
